"use client";

import React, { useState, useEffect, useMemo } from "react";
import {
  Percent,
  RefreshCw,
  RotateCcw,
  Save,
  AlertCircle,
  Users,
  Coffee,
  Wine,
  Gift,
  UserCheck,
  Receipt,
  Sparkles,
  Utensils,
  Search,
  History,
  Calendar,
  ShieldAlert,
  Clock,
  Plus,
  Copy,
  Check,
  FileText
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "react-hot-toast";
import { taxManagementService } from "../apis/taxManagementService";
import { isAdminRole } from "../constants/roles";
import { Link } from "react-router-dom";

const POSITION_ICONS = {
  dealer: Users,
  waitress: Coffee,
  floorsupervisor: Gift,
  bartender: Wine,
  host: UserCheck,
  hosts: UserCheck,
  cashier: Receipt,
  janitor: Sparkles,
  cook: Utensils,
  other: Users
};

const PRESET_RATES = [0, 5, 8, 10, 12, 15];

const formatDateToStandard = (isoString) => {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return String(isoString);
  const day = d.getDate();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

export default function TaxManagement() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Automatically uses current date for audit log recording
  const effectiveDate = useMemo(() => new Date().toISOString().split("T")[0], []);

  // Tax Change Logs state
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [copiedLogId, setCopiedLogId] = useState(null);

  // Manual Log Entry modal
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [customMessage, setCustomMessage] = useState("");
  const [customEffDate, setCustomEffDate] = useState(new Date().toISOString().split("T")[0]);
  const [isSubmittingLog, setIsSubmittingLog] = useState(false);

  // Check user role: Strictly Admin Only
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const isAdmin = isAdminRole(currentUser?.role);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await taxManagementService.getTaxSettings();
      if (res.success && Array.isArray(res.data)) {
        setSettings(res.data);
      } else {
        toast.error(res.error || "Failed to load tax settings");
      }
    } catch (err) {
      console.error("Error loading tax settings:", err);
      toast.error("Failed to load tax settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoadingLogs(true);
      const res = await taxManagementService.getTaxChangeLogs(100);
      if (res.success && Array.isArray(res.data)) {
        setLogs(res.data);
      }
    } catch (err) {
      console.error("Error fetching tax change logs:", err);
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchSettings();
      fetchLogs();
    }
  }, [isAdmin]);

  const handleFieldChange = (positionKey, field, value) => {
    setSettings((prev) =>
      prev.map((item) => {
        if (item.position_key === positionKey) {
          const updated = { ...item, [field]: value };
          if (field === "tax_type" && value === "exempt") {
            updated.rate_percentage = 0;
          }
          if (field === "rate_percentage" && parseFloat(value) > 0 && updated.tax_type === "exempt") {
            updated.tax_type = "percentage";
          }
          return updated;
        }
        return item;
      })
    );
  };

  const handleSaveSetting = async (positionKey) => {
    const item = settings.find((s) => s.position_key === positionKey);
    if (!item) return;

    try {
      setSavingKey(positionKey);
      const res = await taxManagementService.updateTaxSetting(positionKey, {
        rate_percentage: parseFloat(item.rate_percentage) || 0,
        tax_type: item.tax_type,
        is_active: item.is_active,
        description: item.description,
        effective_date: effectiveDate
      });

      if (res.success) {
        toast.success(`Tax settings for ${item.position_name} updated successfully!`);
        if (res.data) {
          setSettings((prev) =>
            prev.map((s) => (s.position_key === positionKey ? { ...s, ...res.data } : s))
          );
        }
        // Refresh logs immediately
        fetchLogs();
      } else {
        toast.error(res.error || "Failed to update tax setting");
      }
    } catch (err) {
      console.error("Error saving tax setting:", err);
      toast.error("An error occurred while saving");
    } finally {
      setSavingKey(null);
    }
  };

  const handleResetDefaults = async () => {
    try {
      setResetting(true);
      const res = await taxManagementService.resetDefaults(effectiveDate);
      if (res.success && Array.isArray(res.data)) {
        setSettings(res.data);
        toast.success("All position tax settings reset to defaults!");
        setShowResetModal(false);
        fetchLogs();
      } else {
        toast.error(res.error || "Failed to reset defaults");
      }
    } catch (err) {
      console.error("Error resetting defaults:", err);
      toast.error("Failed to reset defaults");
    } finally {
      setResetting(false);
    }
  };

  const handleCreateCustomLog = async (e) => {
    e.preventDefault();
    const trimmed = customMessage.trim();
    if (!trimmed) {
      toast.error("Please enter a formatted message");
      return;
    }

    try {
      setIsSubmittingLog(true);
      const res = await taxManagementService.createTaxChangeLog({
        formatted_message: trimmed,
        effective_date: customEffDate,
        position_key: "all",
        position_name: "All Positions"
      });

      if (res.success) {
        toast.success("Tax change milestone logged successfully!");
        setCustomMessage("");
        setIsLogModalOpen(false);
        if (Array.isArray(res.data)) {
          setLogs(res.data);
        } else {
          fetchLogs();
        }
      } else {
        toast.error(res.error || "Failed to record log");
      }
    } catch (err) {
      toast.error("An error occurred while recording log");
    } finally {
      setIsSubmittingLog(false);
    }
  };

  const handleCopyLog = (id, text) => {
    navigator.clipboard?.writeText(text);
    setCopiedLogId(id);
    toast.success("Log message copied to clipboard!");
    setTimeout(() => setCopiedLogId(null), 2000);
  };

  // Filtered settings by search term
  const filteredSettings = useMemo(() => {
    if (!searchTerm) return settings;
    const term = searchTerm.toLowerCase();
    return settings.filter(
      (s) =>
        s.position_name.toLowerCase().includes(term) ||
        s.position_key.toLowerCase().includes(term) ||
        (s.description && s.description.toLowerCase().includes(term))
    );
  }, [settings, searchTerm]);

  // Access Denied Screen for Non-Admins
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full shadow-lg border-red-200">
          <CardHeader className="text-center pb-2">
            <div className="w-14 h-14 mx-auto mb-3 bg-red-100 rounded-full flex items-center justify-center text-red-600">
              <ShieldAlert className="w-8 h-8" />
            </div>
            <CardTitle className="text-xl font-bold text-gray-800">
              Access Restricted
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-gray-600">
              The Tax Management module and deduction rules are strictly restricted to Administrators only.
              Your current role does not have authorization to view or configure tax allocations.
            </p>
            <Button asChild className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold">
              <Link to="/promos">Go to Dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-yellow-500 rounded-xl text-black shadow-md shadow-yellow-500/20">
            <Percent className="h-7 w-7" />
          </div>
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Tax Management</h1>
            <p className="text-sm text-gray-600 mt-0.5">
              Dynamically allocate and audit tipout tax deductions across all positions (Admin Only)
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="outline"
            onClick={() => {
              fetchSettings();
              fetchLogs();
            }}
            disabled={loading || loadingLogs}
            className="flex items-center gap-2 bg-white"
          >
            <RefreshCw className={`h-4 w-4 ${loading || loadingLogs ? "animate-spin" : ""}`} />
            Refresh
          </Button>

          <Button
            variant="outline"
            onClick={() => setShowResetModal(true)}
            disabled={loading}
            className="flex items-center gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 bg-white"
          >
            <RotateCcw className="h-4 w-4" />
            Reset Defaults
          </Button>
        </div>
      </div>

      {/* Search & Position Rules Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2">
          <Percent className="w-5 h-5 text-yellow-600" />
          <h2 className="text-base font-bold text-gray-900">Configured Position Rules</h2>
          <span className="text-xs font-normal text-gray-500">({filteredSettings.length} positions)</span>
        </div>

        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search roles or rules..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 text-xs bg-white"
          />
        </div>
      </div>

      {/* Grid of Position Tax Settings */}
      {loading ? (
        <div className="py-20 text-center text-gray-500">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-yellow-500 mb-2" />
          <p className="text-sm">Loading tax rules...</p>
        </div>
      ) : filteredSettings.length === 0 ? (
        <div className="py-20 text-center text-gray-500 bg-white rounded-lg border border-gray-200">
          <AlertCircle className="h-8 w-8 mx-auto text-gray-400 mb-2" />
          <p className="text-base font-semibold">No positions match "{searchTerm}"</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSettings.map((item) => {
            const Icon = POSITION_ICONS[item.position_key.toLowerCase()] || Users;
            const isSaving = savingKey === item.position_key;

            let previewTax = 0;
            if (item.tax_type === "percentage" && item.is_active) {
              previewTax = Math.round(100 * (parseFloat(item.rate_percentage || 0) / 100));
            }
            const previewPaidOut = 100 - previewTax;

            return (
              <Card
                key={item.position_key}
                className={`bg-white shadow-sm border transition-all duration-200 ${item.is_active ? "border-gray-200 hover:border-yellow-400" : "border-gray-200 opacity-70"
                  }`}
              >
                <CardHeader className="pb-3 border-b border-gray-100 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-100 text-yellow-800 rounded-lg">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-bold text-gray-800">
                        {item.position_name}
                      </CardTitle>
                      <span className="text-[11px] text-gray-400 uppercase tracking-wider">
                        Key: {item.position_key}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={item.is_active}
                        onChange={(e) =>
                          handleFieldChange(item.position_key, "is_active", e.target.checked)
                        }
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-yellow-500"></div>
                    </label>
                  </div>
                </CardHeader>

                <CardContent className="pt-4 space-y-4">
                  {/* Deduction Type Selector */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Deduction Type
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => handleFieldChange(item.position_key, "tax_type", "percentage")}
                        className={`py-1.5 px-3 rounded text-xs font-medium border text-center transition ${item.tax_type === "percentage"
                            ? "bg-yellow-500 text-black border-yellow-500 font-bold"
                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                          }`}
                      >
                        Percentage (%)
                      </button>

                      <button
                        type="button"
                        onClick={() => handleFieldChange(item.position_key, "tax_type", "exempt")}
                        className={`py-1.5 px-3 rounded text-xs font-medium border text-center transition ${item.tax_type === "exempt"
                            ? "bg-blue-600 text-white border-blue-600 font-bold"
                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                          }`}
                      >
                        Exempt (0%)
                      </button>
                    </div>
                  </div>

                  {/* Percentage Rate Input & Slider (when percentage) */}
                  {item.tax_type === "percentage" && (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <label className="text-xs font-semibold text-gray-700">Tax Rate (%)</label>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.5"
                            value={item.rate_percentage}
                            onChange={(e) =>
                              handleFieldChange(item.position_key, "rate_percentage", e.target.value)
                            }
                            className="w-20 h-7 text-right text-xs font-bold bg-white"
                          />
                          <span className="text-xs font-bold text-gray-600">%</span>
                        </div>
                      </div>

                      {/* Slider */}
                      <input
                        type="range"
                        min="0"
                        max="30"
                        step="0.5"
                        value={item.rate_percentage || 0}
                        onChange={(e) =>
                          handleFieldChange(item.position_key, "rate_percentage", e.target.value)
                        }
                        className="w-full accent-yellow-500 cursor-pointer"
                      />

                      {/* Presets */}
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {PRESET_RATES.map((preset) => (
                          <button
                            key={preset}
                            type="button"
                            onClick={() =>
                              handleFieldChange(item.position_key, "rate_percentage", preset)
                            }
                            className={`px-2 py-0.5 rounded text-xs font-medium transition ${parseFloat(item.rate_percentage) === preset
                                ? "bg-yellow-500 text-black font-bold"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                          >
                            {preset}%
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Description / Notes */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Description / Rule Note
                    </label>
                    <Input
                      type="text"
                      value={item.description || ""}
                      onChange={(e) =>
                        handleFieldChange(item.position_key, "description", e.target.value)
                      }
                      placeholder="e.g. Standard rate for this role"
                      className="text-xs h-8 bg-white"
                    />
                  </div>

                  {/* Micro Preview Card */}
                  <div className="bg-gray-50 p-2.5 rounded border border-gray-200 text-xs flex justify-between items-center">
                    <span className="text-gray-500">Preview at $100 tipout:</span>
                    <span className="font-semibold text-gray-800">
                      Tax: <span className="text-red-500">-${previewTax}</span> | Paid Out:{" "}
                      <span className="text-green-600">${previewPaidOut}</span>
                    </span>
                  </div>

                  {/* Actions & Audit Info */}
                  <div className="pt-2 flex items-center justify-between border-t border-gray-100">
                    <span className="text-[11px] text-gray-400">
                      By {item.updated_by || "Admin"}
                    </span>

                    <Button
                      size="sm"
                      onClick={() => handleSaveSetting(item.position_key)}
                      disabled={isSaving}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold text-xs flex items-center gap-1.5"
                    >
                      <Save className="h-3.5 w-3.5" />
                      {isSaving ? "Saving..." : "Save Rule"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ================= TAX CHANGES LOG / AUDIT TRAIL (MOVED TO THE LAST) ================= */}
      <Card className="bg-white shadow-sm border border-gray-200 overflow-hidden">
        <CardHeader className="p-4 bg-gradient-to-r from-gray-900 to-gray-800 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-yellow-500 text-black rounded-lg">
              <History className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-base font-bold text-white flex items-center gap-2">
                Tax Changes Logs
                <span className="text-xs font-normal text-yellow-400 bg-yellow-500/10 border border-yellow-500/30 px-2 py-0.5 rounded-full">
                  Audit Trail
                </span>
              </CardTitle>
              <p className="text-xs text-gray-300 mt-0.5">
                Every rate revision is audited and tracked chronologically
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setCustomMessage("From 1 Aug 2026: dealer 10%, waitress 8%, floor 5%.");
                setIsLogModalOpen(true);
              }}
              className="bg-transparent border-gray-600 text-white hover:bg-gray-700 text-xs flex items-center gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              Record Policy Log
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={fetchLogs}
              disabled={loadingLogs}
              className="text-gray-300 hover:text-white hover:bg-gray-700 h-8 w-8 p-0"
              title="Refresh Logs"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loadingLogs ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-4 space-y-3">
          {loadingLogs ? (
            <div className="py-8 text-center text-gray-500">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto text-yellow-500 mb-2" />
              <p className="text-xs">Loading tax change logs...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="py-8 text-center text-gray-400 text-xs">
              No tax change logs recorded yet.
            </div>
          ) : (
            <div className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
              {logs.map((log) => {
                const isBaseline = log.formatted_message.includes("From 1 Aug 2026");
                return (
                  <div
                    key={log.id}
                    className={`p-3.5 rounded-xl border transition-all flex flex-col md:flex-row md:items-center justify-between gap-3 ${isBaseline
                        ? "bg-amber-50/70 border-yellow-300 shadow-sm"
                        : "bg-gray-50/80 border-gray-200 hover:border-gray-300"
                      }`}
                  >
                    <div className="space-y-1.5 flex-1">
                      {/* Prominent Formatted Log Message */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-bold text-gray-900 bg-white px-3 py-1 rounded-lg border border-gray-200 shadow-xs inline-flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                          {log.formatted_message}
                        </span>

                        {isBaseline && (
                          <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-md bg-yellow-500 text-black">
                            Milestone Policy
                          </span>
                        )}
                      </div>

                      {/* Metadata row */}
                      <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5 text-gray-400" />
                          Changed by: <span className="font-semibold text-gray-700">{log.changed_by || "Admin"}</span>
                        </span>
                        {log.effective_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                            Effective: <span className="font-medium text-gray-700">{formatDateToStandard(log.effective_date)}</span>
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-[11px] text-gray-400">
                          <Clock className="w-3 h-3" />
                          Logged: {new Date(log.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* Copy action */}
                    <div className="flex items-center gap-2 self-end md:self-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCopyLog(log.id, log.formatted_message)}
                        className="text-xs h-8 text-gray-600 hover:text-black flex items-center gap-1 bg-white border border-gray-200 hover:bg-gray-100"
                        title="Copy message"
                      >
                        {copiedLogId === log.id ? (
                          <>
                            <Check className="h-3.5 w-3.5 text-green-600" />
                            <span className="text-green-600 font-semibold">Copied</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-3.5 w-3.5 text-gray-500" />
                            <span>Copy</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Tax Policy Log Entry Modal using Radix Dialog */}
      <Dialog open={isLogModalOpen} onOpenChange={setIsLogModalOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden bg-white border border-gray-200 shadow-2xl rounded-xl">
          <DialogHeader className="px-6 pt-6 pb-2">
            <div className="flex items-center gap-3 text-gray-900">
              <div className="p-2 bg-yellow-100 text-yellow-800 rounded-lg">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold">Record Tax Policy Notice</DialogTitle>
                <p className="text-xs text-gray-500 mt-0.5">
                  Record a milestone tax change entry for official auditing and logs.
                </p>
              </div>
            </div>
          </DialogHeader>

          <form onSubmit={handleCreateCustomLog} className="px-6 pb-6 pt-2 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Effective Date
              </label>
              <Input
                type="date"
                value={customEffDate}
                onChange={(e) => setCustomEffDate(e.target.value)}
                className="text-xs h-9 bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Formatted Log Message <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                placeholder="From 1 Aug 2026: dealer 10%, waitress 8%, floor 5%."
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                className="text-sm h-10 font-mono bg-white"
                autoFocus
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Example: <code className="text-gray-700">From 1 Aug 2026: dealer 10%, waitress 8%, floor 5%.</code>
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsLogModalOpen(false)}
                disabled={isSubmittingLog}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmittingLog || !customMessage.trim()}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
              >
                {isSubmittingLog ? "Saving..." : "Record Milestone"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Confirmation Modal using Radix Dialog */}
      <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden bg-white border border-gray-200 shadow-2xl rounded-xl">
          <DialogHeader className="px-6 pt-6 pb-2">
            <div className="flex items-center gap-3 text-red-600">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="h-6 w-6" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-gray-900">Reset All Tax Settings?</DialogTitle>
                <p className="text-xs text-gray-500 mt-0.5">
                  Confirm reset to system defaults.
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="px-6 pb-6 pt-2 space-y-4">
            <p className="text-sm text-gray-600">
              This will reset all position tax rules back to system default rates (Dealer: 10%,
              Waitress: 8%, Floor Supervisor: 5%, other roles: Exempt 0%). Future tipouts will
              calculate using these reset rates, and an audit log will be appended.
            </p>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowResetModal(false)}
                disabled={resetting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetDefaults}
                disabled={resetting}
                className="bg-red-600 hover:bg-red-700 text-white font-medium"
              >
                {resetting ? "Resetting..." : "Confirm Reset"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
